import { supabase } from './supabase';

export const API_URL = String((import.meta.env.VITE_API_URL as string) || '').replace(/^\uFEFF/,'').trim().replace(/^['\"]|['\"]$/g,'').replace(/\/+$/, '');
export const apiConfigurationIssues: string[] = /^https:\/\//i.test(API_URL) ? [] : ['VITE_API_URL is missing or malformed.'];

export async function api<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Your session has expired. Please sign in again.');

  if (!/^https:\/\//i.test(API_URL)) throw new Error('VITE_API_URL is missing or malformed.');
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) { const requestId=response.headers.get('x-request-id')||payload.request_id; throw new Error(`${payload.error || `Request failed (${response.status})`}${requestId?` (Request ID: ${requestId})`:''}`); }
  return payload as T;
}

export async function uploadAccountingDocument(args: {
  sourceModule: string;
  sourceId: string;
  documentType?: string;
  file: File;
}) {
  const signed = await api<{ path: string; token: string }>('/api/documents/upload-url', {
    method: 'POST',
    body: JSON.stringify({
      source_module: args.sourceModule,
      source_id: args.sourceId,
      file_name: args.file.name,
      content_type: args.file.type,
    }),
  });

  const { error } = await supabase.storage
    .from('accounting-documents')
    .uploadToSignedUrl(signed.path, signed.token, args.file, { contentType: args.file.type, upsert: false });
  if (error) throw error;

  return api('/api/documents/complete', {
    method: 'POST',
    body: JSON.stringify({
      source_module: args.sourceModule,
      source_id: args.sourceId,
      document_type: args.documentType || 'supporting_document',
      file_name: args.file.name,
      storage_path: signed.path,
      mime_type: args.file.type,
      file_size: args.file.size,
    }),
  });
}


export async function downloadApi(path: string): Promise<{ blob: Blob; filename: string }> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Your session has expired. Please sign in again.');

  if (!/^https:\/\//i.test(API_URL)) throw new Error('VITE_API_URL is missing or malformed.');
  const response = await fetch(`${API_URL}${path}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
    cache: 'no-store',
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error || `Download failed (${response.status})`);
  }

  const disposition = response.headers.get('content-disposition') || '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const quoted = /filename="([^"]+)"/i.exec(disposition)?.[1];
  const plain = /filename=([^;]+)/i.exec(disposition)?.[1]?.trim();
  const filename = encoded ? decodeURIComponent(encoded) : (quoted || plain || 'magna-accounting-backup.json');
  return { blob: await response.blob(), filename };
}

export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
